import { Controller } from "@hotwired/stimulus"
import { calculate } from "../modules/salary_lab/calculator_api"
import { formatCurrency, parseNumber } from "../modules/salary_lab/formatter"
import { SmartDebounce, detectEventType } from "../modules/salary_lab/smart_debounce"
import { writeGenericToUrl, readGenericFromUrl } from "../modules/salary_lab/url_state"
import { trackCalculatorUsed } from "../modules/salary_lab/ga4_tracker"

const URL_MAP = { current_age: "age", current_salary: "s", current_balance: "bal", contribution_rate: "rate", fund_type: "fund", salary_growth: "growth" }
const URL_TYPES = { current_age: "number", current_salary: "number", current_balance: "number" }

export default class extends Controller {
  static targets = [
    "ageInput", "salaryInput", "balanceInput",
    "contributionSelect", "fundSelect", "salaryGrowthInput",
    "advancedOptions", "advancedToggle",
    "resultsPanel", "loadingIndicator", "introContent",
    "projectedExpected", "projectedRange",
    "projectedExpectedReal", "projectedRangeReal",
    "employeeAnnual", "employerAnnual", "governmentAnnual", "totalAnnual",
    "fortnightlyCost", "yearsToRetirement",
    "totalContributions", "totalReturns",
    "chartCanvas",
    "liveRegion"
  ]

  static values = {
    inflation: { type: Number, default: 2.5 },
    retirementAge: { type: Number, default: 65 }
  }

  connect() {
    this._debounce = new SmartDebounce(() => this._performCalculation(), { minDigits: 4 })
    this._chart = null
    this._prefillFromUrl()
    this._renderPrerenderedExtras()
  }

  disconnect() {
    this._debounce.cancel()
    if (this._chart) this._chart.destroy()
  }

  inputChanged(event) { this._debounce.schedule(detectEventType(event), this.salaryInputTarget.value) }
  optionChanged() { this._debounce.schedule("toggle", this.salaryInputTarget.value) }

  toggleAdvanced(event) {
    event.preventDefault()
    const visible = this.advancedOptionsTarget.classList.toggle("hidden")
    this.advancedToggleTarget.textContent = visible ? "Show more options" : "Hide options"
  }

  submitForm(event) {
    event.preventDefault()
    this._debounce.schedule("submit")
  }

  async _performCalculation() {
    const age = parseInt(this.ageInputTarget.value, 10)
    const salary = parseNumber(this.salaryInputTarget.value)
    if (!age || !salary || salary <= 0 || age >= this.retirementAgeValue) return

    this._showLoading()

    try {
      const params = {
        type: "kiwisaver",
        current_age: age,
        current_salary: salary,
        current_balance: this.hasBalanceInputTarget ? parseNumber(this.balanceInputTarget.value) || 0 : 0,
        contribution_rate: this.contributionSelectTarget.value,
        fund_type: this.fundSelectTarget.value
      }

      if (this.hasSalaryGrowthInputTarget) {
        params.salary_growth = this.salaryGrowthInputTarget.value
      }

      const data = await calculate(params)
      this._renderResults(data)
      writeGenericToUrl(params, URL_MAP)
      trackCalculatorUsed("kiwisaver", { salaryAmount: salary })
    } catch (error) {
      if (this.hasLiveRegionTarget) {
        this.liveRegionTarget.textContent = error.message || "Calculation failed. Please try again."
      }
    }
  }

  _renderResults(data) {
    this.projectedExpectedTarget.textContent = formatCurrency(data.projected_expected)
    this.projectedRangeTarget.textContent =
      `${formatCurrency(data.projected_pessimistic)} – ${formatCurrency(data.projected_optimistic)}`

    const years = data.years_to_retirement
    const deflator = Math.pow(1 + this.inflationValue / 100, years)
    if (this.hasProjectedExpectedRealTarget) {
      this.projectedExpectedRealTarget.textContent = formatCurrency(data.projected_expected / deflator)
    }
    if (this.hasProjectedRangeRealTarget) {
      this.projectedRangeRealTarget.textContent =
        `${formatCurrency(data.projected_pessimistic / deflator)} – ${formatCurrency(data.projected_optimistic / deflator)}`
    }

    this.employeeAnnualTarget.textContent = formatCurrency(data.employee_annual)
    this.employerAnnualTarget.textContent = formatCurrency(data.employer_annual)
    this.governmentAnnualTarget.textContent = formatCurrency(data.government_annual)
    this.totalAnnualTarget.textContent = formatCurrency(data.total_annual_contribution)
    this.fortnightlyCostTarget.textContent = formatCurrency(data.fortnightly_cost)
    this.yearsToRetirementTarget.textContent = `${data.years_to_retirement} years`
    this.totalContributionsTarget.textContent = formatCurrency(data.total_contributions)
    this.totalReturnsTarget.textContent = formatCurrency(data.total_returns_expected)

    this._renderChart(data.yearly_balances)
    this._showResults()

    if (this.hasLiveRegionTarget) {
      const realExpected = formatCurrency(data.projected_expected / deflator)
      this.liveRegionTarget.textContent =
        `Projected KiwiSaver balance at ${this.retirementAgeValue}: ${formatCurrency(data.projected_expected)} ` +
        `(${realExpected} in today's dollars). ` +
        `Range: ${formatCurrency(data.projected_pessimistic)} to ${formatCurrency(data.projected_optimistic)}.`
    }
  }

  async _renderChart(balances) {
    if (!this.hasChartCanvasTarget || !balances || balances.length === 0) return

    try {
      const { Chart, LineController, Filler, LineElement, PointElement, LinearScale, CategoryScale, Tooltip } =
        await import("chart.js")
      Chart.register(LineController, Filler, LineElement, PointElement, LinearScale, CategoryScale, Tooltip)

      if (this._chart) this._chart.destroy()

      const labels = balances.map(b => b.age)
      this._chart = new Chart(this.chartCanvasTarget, {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: "Optimistic",
              data: balances.map(b => b.optimistic),
              borderColor: "rgba(14, 116, 144, 0.3)",
              backgroundColor: "rgba(14, 116, 144, 0.05)",
              fill: "+1",
              tension: 0.3,
              pointRadius: 0
            },
            {
              label: "Expected",
              data: balances.map(b => b.expected),
              borderColor: "#0E7490",
              backgroundColor: "rgba(14, 116, 144, 0.15)",
              fill: "+1",
              borderWidth: 2,
              tension: 0.3,
              pointRadius: 0
            },
            {
              label: "Pessimistic",
              data: balances.map(b => b.pessimistic),
              borderColor: "rgba(14, 116, 144, 0.3)",
              backgroundColor: "transparent",
              fill: false,
              borderDash: [5, 5],
              tension: 0.3,
              pointRadius: 0
            }
          ]
        },
        options: {
          responsive: true,
          interaction: { mode: "index", intersect: false },
          plugins: {
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString()}`
              }
            }
          },
          scales: {
            x: { title: { display: true, text: "Age" } },
            y: {
              title: { display: true, text: "Balance ($)" },
              ticks: { callback: (v) => `$${(v / 1000).toFixed(0)}K` }
            }
          }
        }
      })
    } catch {
      // Chart.js not available — skip
    }
  }

  _prefillFromUrl() {
    const p = readGenericFromUrl(URL_MAP, URL_TYPES)
    if (!p.current_salary) return
    this.salaryInputTarget.value = p.current_salary
    if (p.current_age != null) this.ageInputTarget.value = p.current_age
    if (p.current_balance != null && this.hasBalanceInputTarget) this.balanceInputTarget.value = p.current_balance
    if (p.contribution_rate != null) this.contributionSelectTarget.value = p.contribution_rate
    if (p.fund_type) this.fundSelectTarget.value = p.fund_type
    if (p.salary_growth != null && this.hasSalaryGrowthInputTarget) this.salaryGrowthInputTarget.value = p.salary_growth
    if (this.element.dataset.prerendered !== "true") this._performCalculation()
  }

  _renderPrerenderedExtras() {
    if (this.element.dataset.prerendered !== "true") return
    const json = this.element.dataset.prerenderedResults
    if (!json) return
    try {
      const data = JSON.parse(json)
      const years = data.years_to_retirement
      const deflator = Math.pow(1 + this.inflationValue / 100, years)
      if (this.hasProjectedExpectedRealTarget) {
        this.projectedExpectedRealTarget.textContent = formatCurrency(data.projected_expected / deflator)
      }
      if (this.hasProjectedRangeRealTarget) {
        this.projectedRangeRealTarget.textContent =
          `${formatCurrency(data.projected_pessimistic / deflator)} – ${formatCurrency(data.projected_optimistic / deflator)}`
      }
      if (data.yearly_balances) {
        requestAnimationFrame(() => this._renderChart(data.yearly_balances))
      }
    } catch { /* ignore parse errors */ }
  }

  _showLoading() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.remove("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.add("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }

  _showResults() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.add("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.remove("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }
}
